<!--
 * @Description: 
 * @Date: 2024-03-15 13:09:22
-->
<template>
  <BaseForm
    ref="FormRef"
    :model="formData"
    label-suffix="："
    label-position="right"
    :disabled="disabled"
  >
    <BaseFormItem
      v-for="item in columns"
      :key="item.prop"
      :prop="item.prop"
      :label="item.label"
      :rules="rules[item.prop]"
      :label-width="item.labelWidth"
    >
      <template v-if="item.type === 'input'">
        <BaseInput
          style="width: 100%"
          :disabled="disableKeys.has(item.prop)"
          v-model="formData[item.prop]"
          :placeholder="`请输入${item.label}`"
          v-bind="item.config"
        ></BaseInput>
      </template>
      <template v-if="item.type === 'img'">
        <template v-if="disabled && !formData[item.prop]">
          <span>-</span>
        </template>
        <BaseUpload
          v-else
          style="width: 100%"
          :file-list="
            formData[item.prop]
              ? [
                  {
                    url: formData[item.prop],
                    name: '器械图片',
                    status: 'success',
                    uid: 1
                  }
                ]
              : []
          "
          class="custom-upload"
          :class="{ 'has-img': formData[item.prop] }"
          auto-upload
          drag
          list-type="picture-card"
          :http-request="
            async ({ file }) => {
              Reflect.set(formData, item.prop, await uploadFile(file));
              Reflect.set(formData, `file_${item.prop}`, file);
            }
          "
          :before-remove="
            () => {
              Reflect.set(formData, item.prop, '');
              return true;
            }
          "
          :disabled="disableKeys.has(item.prop) || disabled"
          :accept="['image/*']"
          @error="
            ({ msg }) => {
              XrkMessage.error(msg);
            }
          "
          :before-selected="
            (() => {
              let show = false;
              return file => {
                if (!file.type) {
                  !show &&
                    (show = true) &&
                    XrkMessage.error({
                      message: '不支持上传此类文件',
                      onClose: () => (show = false)
                    });
                  throw '不支持上传此类文件';
                }
                return false;
              };
            })()
          "
        ></BaseUpload>
      </template>
      <template v-if="item.type === 'search'">
        <div style="display: flex; gap: 12px; width: 100%">
          <BaseInput
            v-bind="item.config"
            style="flex-grow: 1"
            :disabled="disableKeys.has(item.prop)"
            v-model="formData[item.prop]"
            :placeholder="`请输入${item.label}`"
          ></BaseInput>
          <BaseButton
            :disabled="!formData[item.prop] || disableKeys.has(item.prop)"
            :click="async () => await item.searchFn?.(formData[item.prop])"
          >
            查询
          </BaseButton>
        </div>
      </template>
    </BaseFormItem>
  </BaseForm>
</template>

<script setup lang="tsx">
import {
  BaseButton,
  BaseForm,
  BaseFormItem,
  BaseFormRef,
  BaseInput,
  BaseUpload,
  XrkMessage
} from 'xrk-components';
import { defineProps, ref, defineExpose } from 'vue';
import { Device } from '../../data-source/types';
import {
  PRODUCT_DEVICE,
  PRODUCT_SOURCE_LIBRARY,
  PRODUCT_SOURCE_PROJECT
} from '../../config';
import { getProductOtherInfo } from '../../data-source/api';

type EditRow = Partial<Device['row']>;
const props = defineProps<{
  isAdd?: boolean;
  disabled?: boolean;
  row: EditRow;
}>();

const FormRef = ref<BaseFormRef>();

const Reflect = window.Reflect;
const uploadFile = async (file: File) => {
  return URL.createObjectURL(file);
};

const formData = ref<{ [key in keyof typeof props.row]: any }>({
  ...props.row
});
const needDealKeys: Array<keyof EditRow> = ['name', 'guige', 'scope'];
const disableKeys = ref<Set<keyof EditRow>>(new Set());
if (
  [PRODUCT_SOURCE_LIBRARY, PRODUCT_SOURCE_PROJECT].includes(props.row.source) ||
  props.isAdd
) {
  disableKeys.value = new Set(needDealKeys);
  if (!props.isAdd) {
    disableKeys.value.add('approvalNumber');
  }
}

const columns: Array<{
  type?: 'input' | 'img' | 'search';
  prop: any;
  label: string;
  required?: boolean;
  labelWidth?: string;
  config?: any;
  disable?: boolean;
  searchFn?: (searchVal: string | number | undefined) => Promise<void> | void;
}> = [
  {
    prop: 'approvalNumber',
    label: '注册证编号',
    type: 'search',
    config: { maxlength: 20, clearable: true },
    required: true,
    searchFn: async searchVal => {
      const { data } = await getProductOtherInfo({
        approvalNumber: searchVal,
        type: PRODUCT_DEVICE
      });
      const isEmpty = !data || Object.keys(data).length === 0;
      if (isEmpty) XrkMessage.error('暂未查询到信息');
      needDealKeys.forEach(key => {
        if (isEmpty) {
          disableKeys.value.delete(key);
          formData.value[key] = '';
        } else {
          disableKeys.value.add(key);
          formData.value[key] = data?.[key] || '-';
        }
      });
      setTimeout(() => FormRef.value.validateField(needDealKeys), 0);
    }
  },
  {
    prop: 'name',
    label: '器械名称',
    type: 'input',
    required: true,
    config: { maxlength: 20, clearable: true }
  },
  {
    prop: 'guige',
    label: '型号规格',
    type: 'input',
    required: true,
    config: { maxlength: 200, clearable: true }
  }
];

const rules = columns.reduce((prev, cur) => {
  prev[cur.prop] = cur.required
    ? [
        {
          required: true,
          message: `请${
            ['input', 'search'].includes(cur.type) ? '输入' : '选择'
          }${cur.label}`,
          trigger: 'blur'
        }
      ]
    : [];
  return prev;
}, {});

defineExpose({
  getFormData: async () => {
    await FormRef.value.validate();
    const res = { ...formData.value };
    Array.from(disableKeys.value).forEach(key => {
      if (res[key] === '-') {
        res[key] = '';
      }
    });
    return res;
  }
});
</script>

<style lang="scss" scoped>
.custom-upload {
  ::v-deep {
    .xrk-upload {
      --xrk-upload-dragger-padding-horizontal: 20px;
    }
  }
}
.has-img {
  ::v-deep {
    .xrk-upload__list {
      margin-top: 0;
    }
    .xrk-upload.is-drag,
    .xrk-upload__tip {
      display: none;
    }
    .xrk-upload__item_img {
      width: 100%;
      margin: 0;
    }
  }
}
</style>
